import { useState, useEffect, useCallback, useRef } from 'react';
import { AIWorker, SystemStats, TelegramNotification, Phase } from '../types';
import { mockWorkers, mockStats, mockNotifications } from '../data/mockWorkers';
import { dispatchSimEvent, isSimEvent, useSimStore } from '../sim';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

interface WSState {
  workers: AIWorker[];
  stats: SystemStats;
  notifications: TelegramNotification[];
  phases: Phase[];
  connected: boolean;
  refreshPhases: () => void;
}

export function useWebSocket(url: string): WSState {
  const [workers, setWorkers] = useState<AIWorker[]>(mockWorkers);
  const [stats, setStats] = useState<SystemStats>(mockStats);
  const [notifications, setNotifications] = useState<TelegramNotification[]>(mockNotifications);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<NodeJS.Timeout>(undefined);

  // Fetch phases from REST API
  const refreshPhases = useCallback(() => {
    fetch(`${API}/api/phases`)
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setPhases(data); })
      .catch(() => {});
  }, []);

  const connect = useCallback(() => {
    try {
      // Attach the same Bearer token the REST API uses. Browsers
      // forbid setting headers on WebSocket handshakes, so the only
      // portable options are query string or Sec-WebSocket-Protocol.
      // We go with ?token= — trivial on both sides. Don't open the
      // socket at all without a token; backend would just 4401-close
      // us, which creates a reconnect storm.
      const token = localStorage.getItem('lato.auth.token.v1');
      if (!token) {
        console.log('[WS] no auth token — skipping connect');
        return;
      }
      const sep = url.includes('?') ? '&' : '?';
      const ws = new WebSocket(`${url}${sep}token=${encodeURIComponent(token)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        console.log('[WS] Connected to Lato backend');
        refreshPhases(); // Load phases on connect
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          // Simulation events fan out to the sim store — these are
          // cheap deltas used exclusively by the 3D scene.
          if (isSimEvent(msg)) {
            dispatchSimEvent(msg);
            return;
          }

          switch (msg.type) {
            case 'init':
              if (msg.data.workers?.length) setWorkers(msg.data.workers);
              if (msg.data.stats) setStats(msg.data.stats);
              if (msg.data.notifications?.length) setNotifications(msg.data.notifications);
              if (msg.data.phases?.length) setPhases(msg.data.phases);
              break;

            case 'worker:updated':
              setWorkers((prev) =>
                prev.map((w) => (w.id === msg.data.worker.id ? { ...w, ...msg.data.worker } : w))
              );
              break;

            case 'task:started':
            case 'task:completed':
            case 'task:error':
              setWorkers((prev) =>
                prev.map((w) => {
                  if (w.name === msg.data.workerName) {
                    return {
                      ...w,
                      currentTask: msg.data.task,
                      status: msg.type === 'task:completed' ? 'done' :
                              msg.type === 'task:error' ? 'error' : 'working',
                    };
                  }
                  return w;
                })
              );
              const nType: TelegramNotification['type'] =
                msg.type === 'task:started' ? 'task_start' :
                msg.type === 'task:completed' ? 'task_complete' : 'error';
              setNotifications((prev) => [{
                id: `ws-${Date.now()}`,
                timestamp: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
                worker: msg.data.workerName,
                message: msg.data.task?.title || '',
                type: nType,
              } as TelegramNotification, ...prev].slice(0, 50));
              // Refresh phases on task events
              refreshPhases();
              break;

            case 'commit':
              setNotifications((prev) => [{
                id: `ws-${Date.now()}`,
                timestamp: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
                worker: msg.data.workerName,
                message: msg.data.message,
                type: 'commit' as const,
              } as TelegramNotification, ...prev].slice(0, 50));
              break;

            case 'phase:updated':
              if (msg.data.phases) setPhases(msg.data.phases);
              else refreshPhases();
              break;

            case 'stats:updated':
              if (msg.data) setStats(msg.data);
              break;

            case 'task:tokens': {
              // Live token counts from claude-runner. Find the
              // worker by id and merge into their currentTask so
              // the WorkerDetail panel can show running cost.
              const { workerId, taskId, inputTokens, outputTokens, costUsd, model } = msg.data || {};
              if (!workerId) break;
              setWorkers((prev) =>
                prev.map((w) => {
                  if (w.id !== workerId || !w.currentTask) return w;
                  if (taskId && w.currentTask.id !== taskId) return w;
                  return {
                    ...w,
                    currentTask: {
                      ...w.currentTask,
                      inputTokens, outputTokens, costUsd, model,
                    },
                  };
                }),
              );
              break;
            }
          }
        } catch (err) {
          console.error('[WS] Parse error:', err);
        }
      };

      ws.onclose = (ev) => {
        setConnected(false);
        // 4401 = our private "unauthorized" close code from the
        // backend. Don't reconnect-storm — the token is stale; drop
        // it and let the page reload to land on /login.
        if (ev.code === 4401) {
          console.warn('[WS] unauthorized — clearing token + reloading');
          localStorage.removeItem('lato.auth.token.v1');
          setTimeout(() => window.location.reload(), 100);
          return;
        }
        // 4403 = authenticated but no main-admin access. The token is
        // valid — do NOT clear it — but the backend tells us not to
        // retry: a confined account will never pass this gate, so
        // reconnecting would just storm it every 3 seconds.
        if (ev.code === 4403) {
          console.warn('[WS] forbidden (no main-admin access) — not retrying');
          return;
        }
        console.log('[WS] Disconnected. Reconnecting in 3s...');
        reconnectTimer.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => { ws.close(); };
    } catch {
      setConnected(false);
    }
  }, [url, refreshPhases]);

  useEffect(() => {
    connect();
    return () => {
      wsRef.current?.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  // Sim store GC tick — evicts expired bubbles/commits/alerts
  useEffect(() => {
    const gc = useSimStore.getState().gc;
    const t = setInterval(gc, 1000);
    return () => clearInterval(t);
  }, []);

  return { workers, stats, notifications, phases, connected, refreshPhases };
}
